import { useCallback, useEffect, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface VenIssue {
  ven: string;
  error: string;
}

export interface SystemRegistryDocument {
  venIssues?: VenIssue[];
  [key: string]: unknown;
}

export interface SystemRegistryStatus {
  lastRunAt?: string;
  lastSuccessAt?: string;
  lastError?: string;
  nextRunAt?: string;
  isRunning: boolean;
  entityCount?: number;
  intervalMs?: number;
  configured: boolean;
}

/**
 * Splits a system VEN such as
 * `ven:app:domain.key=hydra:system.key=dev-portal` into its registry
 * coordinates. The entity name can't be used instead: it is `{domain}-{key}`
 * and keys themselves contain dashes, so it isn't reversible.
 */
export function parseSystemVen(ven?: string): { domain: string; key: string } | null {
  if (!ven) return null;
  const domain = ven.match(/domain\.key=([^:]+)/)?.[1];
  const key = ven.match(/system\.key=([^:]+)/)?.[1];
  return domain && key ? { domain, key } : null;
}

/**
 * The tab badge and the tab body both need the same document. Sharing the
 * in-flight promise keeps a system page to one request instead of two.
 */
const documentCache = new Map<string, Promise<SystemRegistryDocument>>();

export function invalidateSystemRegistryDocument(cacheKey?: string): void {
  if (cacheKey) documentCache.delete(cacheKey);
  else documentCache.clear();
}

async function fetchDocument(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  domain: string,
  key: string,
): Promise<SystemRegistryDocument> {
  const base = await discoveryApi.getBaseUrl('portal');
  const response = await fetchApi.fetch(
    `${base}/system-registry/systems/${encodeURIComponent(domain)}/${encodeURIComponent(key)}`,
  );
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body?.error ?? `Request failed with ${response.status}`);
  }
  return body;
}

export function useSystemRegistryDocument(coordinates: { domain: string; key: string } | null) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [document, setDocument] = useState<SystemRegistryDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const cacheKey = coordinates ? `${coordinates.domain}/${coordinates.key}` : null;

  const load = useCallback(
    async (force = false) => {
      if (!coordinates || !cacheKey) return;
      if (force) documentCache.delete(cacheKey);

      setLoading(true);
      setError(null);
      try {
        let pending = documentCache.get(cacheKey);
        if (!pending) {
          pending = fetchDocument(discoveryApi, fetchApi, coordinates.domain, coordinates.key);
          documentCache.set(cacheKey, pending);
        }
        setDocument(await pending);
      } catch (e: any) {
        // A rejected promise must not stay cached, or the error becomes sticky.
        documentCache.delete(cacheKey);
        setError(e.message ?? 'Failed to load System Registry data');
        setDocument(null);
      } finally {
        setLoading(false);
      }
    },
    [cacheKey, coordinates, discoveryApi, fetchApi],
  );

  useEffect(() => {
    load();
  }, [load]);

  return { document, error, loading, reload: () => load(true) };
}
